import { useLocale, useTranslation } from '../i18n/I18nProvider'
import { formatBuildDate } from '../pwa/formatBuildDate'
import { BUILD } from '../pwa/version'
import { Bidi } from './Bidi'
import { Icon } from './icons/Icon'

/**
 * The settings sidebar (D30 + D133), ported from `prototype/screens/shared.js`'s `drawer()`.
 *
 * **The Devices row arrived with the page it opens (P15 surface 1).** It was absent rather than
 * present-and-disabled for as long as `devices` was a `RoutePlaceholder` — §7.3's rule that *"an
 * entry that opens nothing is worse than no entry"*, the same shape as D87's reserved latch
 * states. That is the whole of the sequencing: the row and its screen land together, and neither
 * ships without the other.
 *
 * `onAddToHomeScreen` is nullable for the same reason: in an installed (standalone) app there
 * is nothing left to add, so the caller passes `null` and the row is not rendered — rather
 * than rendering a row that offers to install an already-installed app.
 *
 * The identity block at the top shows D42's **self-name** — never a `memberLabel`, which is
 * gate-scoped and belongs to the manager who wrote it (§5.4: the user always sees his own
 * name). **It is a label and nothing else since D188:** the pencil beside it, and the
 * `onEditName`/`editNameDisabled` props behind it, moved to the Preferences screen the first row
 * below now opens. The name stays here because this is where a resident checks *who they are
 * signed in as*; only the affordance to change it left.
 *
 * **The account-deletion control (P19 §3) is not here.** It left this sidebar for Settings →
 * Devices, and D194 moved it again onto the **My account** screen the first row opens — see
 * `PreferencesScreen` for the pill and `App` for the dialog it opens.
 */
export function Drawer({
  open,
  onClose,
  name,
  onPreferences,
  onAddToHomeScreen,
  addToHomeScreenDot = false,
  onInstallGate,
  onDevices,
  onOpenLanguage,
  languageDisabled = false,
  onAbout,
  onUpdate,
  updateAvailable = false,
  updateApplying = false,
  updateLabelKey = 'update',
  onSignOut,
}: {
  readonly open: boolean
  onClose(): void
  /** Absent only defensively — a session with no self-name cannot reach a route with a drawer. */
  readonly name?: string
  /**
   * Opens D188's Preferences screen — the self-name edit and the appearance pill. Required, not
   * nullable, and not disabled offline: the screen always exists, and half of what it carries
   * works with no network. The *name* row inside it is what refuses offline, which is where that
   * refusal belongs now that the two settings sit side by side.
   */
  onPreferences(): void
  readonly onAddToHomeScreen: (() => void) | null
  /**
   * The app is installable and not yet installed. Unlike `updateAvailable`, this governs only the
   * dot — the row's own existence is `onAddToHomeScreen`'s to decide, and the caller drives both
   * from one predicate.
   */
  readonly addToHomeScreenDot?: boolean
  onInstallGate(): void
  /** Opens Settings → Devices (§7.11). Required, not nullable: the screen it opens always exists. */
  onDevices(): void
  /** Opens `LanguagePopover`, the picker among the two live locales and the two coming-soon ones. */
  onOpenLanguage(): void
  /**
   * Offline, or a save already in flight. Changing the language is a `PATCH /users/me`, so it is
   * a cloud write and offline is read-only — the same rule and the same treatment the rename
   * carries on the Preferences screen, rather than a local flip that the next boot would revert.
   */
  readonly languageDisabled?: boolean
  onAbout(): void
  /**
   * **Applies the waiting shell (D137, one-tap since D155).** Not "opens the update surface" —
   * there is no longer one, and the row is the whole interaction. It ends in a page reload.
   */
  onUpdate(): void
  /** A newer shell is waiting. Governs whether the row renders at all, not just its dot. */
  readonly updateAvailable?: boolean
  /** The apply is under way and the page is on its way out. Renames the row and refuses a second tap. */
  readonly updateApplying?: boolean
  /**
   * **Which rail is behind the row, said as a label rather than as a platform (D266).**
   *
   * `update` is *Update now* — the PWA's one-tap apply and Play's *start the download* tap alike.
   * `updateRestart` is FLEXIBLE's second tap, where the binary is already on the device and the
   * word the resident presses has to be *Restart*. `updateInStore` is iOS, where the tap genuinely
   * leaves the app for a listing.
   *
   * **Optional with a default, so every existing caller and test is byte-unchanged** — this is an
   * addition to a shipped row rather than a rewrite of one.
   */
  readonly updateLabelKey?: 'update' | 'updateRestart' | 'updateInStore'
  onSignOut(): void
}): JSX.Element {
  const { t } = useTranslation('drawer')
  const { t: tCommon } = useTranslation('common')
  const { dir, locale } = useLocale()
  const buildDate = BUILD.builtAt === undefined ? undefined : formatBuildDate(BUILD.builtAt, locale)

  return (
    <>
      <div className={`backdrop ${open ? 'show' : ''}`.trim()} onClick={onClose} aria-hidden="true" />
      <aside className={`drawer ${open ? 'show' : ''}`.trim()} aria-label={t('title')} dir={dir}>
        <div className="drawer-head">
          <span>{t('title')}</span>
          <button className="drawer-close" type="button" onClick={onClose} aria-label={tCommon('close')}>
            <Icon name="x" size={18} />
          </button>
        </div>
        {name === undefined ? null : (
          <div className="drawer-identity">
            <span className="drawer-identity-icon" aria-hidden="true">
              <Icon name="user" size={16} />
            </span>
            {/*
              A raw `<bdi>` rather than the `Bidi` component: that one forces `dir="ltr"` for
              codes and phone numbers, and a self-name may be Hebrew, Latin, or mixed (PEC-3).
              `<bdi>` on its own isolates and resolves direction from the content.
            */}
            <bdi className="drawer-identity-name">{name}</bdi>
          </div>
        )}
        {/*
          **First row, directly under the name, and that placement is the whole of D188's
          sidebar half.** It replaces the pencil that used to sit inside the identity block: an
          icon-only control beside a name says *edit this name* and nothing else, while a row
          under it can carry two settings and the next one after that. It is above
          `addToHomeScreen` because the rows below it are all *do a thing* — install, add, update,
          sign out — and this is the only one that is *change how the app is*.
        */}
        <button className="row" type="button" data-interactive="true" onClick={onPreferences}>
          <span className="l">
            <Icon name="settings" size={15} /> {t('preferences')}
          </span>
          <span className="r">
            <Icon name="chevron-right" size={14} />
          </span>
        </button>
        {onAddToHomeScreen === null ? null : (
          <button className="row" type="button" data-interactive="true" onClick={onAddToHomeScreen}>
            <span className="l">
              <Icon name="share" size={15} /> {t('addToHomeScreen')}
              {/* The same dot as the Update row's, for the same sentence: the hamburger says a row
                  in here wants you, and this says which. It is the whole of the reminder now that
                  the card itself is shown once, on the sign-in screen, and never repeated. */}
              {addToHomeScreenDot ? (
                <span className="row-dot" aria-label={t('addToHomeScreenAvailable')} />
              ) : null}
            </span>
            <span className="r">
              <Icon name="chevron-right" size={14} />
            </span>
          </button>
        )}
        <button className="row" type="button" data-interactive="true" onClick={onInstallGate}>
          <span className="l">
            <Icon name="hard-hat" size={15} /> {t('installGate')}
          </span>
          <span className="r">
            <Icon name="chevron-right" size={14} />
          </span>
        </button>
        <button className="row" type="button" data-interactive="true" onClick={onDevices}>
          <span className="l">
            <Icon name="smartphone" size={15} /> {t('devices')}
          </span>
          <span className="r">
            <Icon name="chevron-right" size={14} />
          </span>
        </button>
        <button
          className="row"
          type="button"
          data-interactive="true"
          onClick={onOpenLanguage}
          disabled={languageDisabled}
        >
          <span className="l">
            <Icon name="globe" size={15} /> {t('language')}
          </span>
          {/*
            The row shows the language you are **in**, not the one you would switch to — the same
            reading as `AppBar`'s layout glyph, which names the current mode. Both catalogs write
            their own name in their own script, so this value is never translated: `languageValue`
            is "עברית" in `he` and "English" in `en`.
          */}
          <span className="r">
            {t('languageValue')}
            <Icon name="chevron-right" size={14} />
          </span>
        </button>
        <button className="row" type="button" data-interactive="true" onClick={onAbout}>
          <span className="l">
            <Icon name="info" size={15} /> {t('about')}
          </span>
          <span className="r">
            <Icon name="chevron-right" size={14} />
          </span>
        </button>
        {/*
          Update sits beside About rather than inside it: D136 made About what the *product* is —
          brand, description, two contact facts — and this is which build of it you are running.
          The row used to render unconditionally, on the theory that it was also the only way to
          read the version; now that the version lives at the bottom of the About screen, that
          reason is gone, and a row that opens a sheet with nothing to apply and nothing to show
          is the flash-or-blank the resident saw. So it renders only when a shell is actually
          waiting — the dot is no longer decoration on top of an always-present row, it is the
          row's own reason to exist.

          **D155 made it the action rather than the door to it.** The sheet it used to open said
          which build was running and carried one button; the version moved to About and the row
          only exists when there is something to apply, so the sheet had become a confirmation step
          in front of an answer the resident had already given by tapping a row labelled *Update*.
          It is a one-tap apply now — no chevron, because nothing opens — and D137's invariant is
          untouched: this is still the only thing in the app that sends `SKIP_WAITING`, and it still
          only fires on a tap.

          `updateApplying` disables the row and renames it in place. The reload takes the component
          with it, so this state is short — but it is the difference between a tap that looks
          ignored and one that is under way. **On D266's store rail it is FLEXIBLE's download**,
          which is not short at all and which the app survives — the same prop, carrying a longer
          wait, because a background download *is* an apply under way.

          **No `.r` chevron in any variant, `updateInStore` included.** The temptation is real
          there — that tap genuinely leaves the app for a listing — but a chevron in this sidebar
          means *this opens a screen inside the app*, which is what D155 removed from this very
          row when the sheet went. A row that leaves the app is still a one-tap action, and it says
          so in words.
        */}
        {updateAvailable ? (
          <button
            className="row"
            type="button"
            data-interactive="true"
            onClick={onUpdate}
            disabled={updateApplying}
          >
            <span className="l">
              <Icon name="refresh-cw" size={15} /> {updateApplying ? t('updating') : t(updateLabelKey)}
              {updateApplying ? null : <span className="row-dot" aria-label={t('updateAvailable')} />}
            </span>
          </button>
        ) : null}
        {/*
          **D190's two legal rows moved to the About screen** — the published privacy policy and
          Terms of use are still one tap from here, one screen further down. The sidebar is where a
          resident goes to *do* something; those two are things to *read*, and About is where the
          product already describes itself and already links out to the same host. Do not restore
          them here: the destinations did not change, only which surface carries them, and a second
          entry point to a legal page is a second one to keep in step with the site.
        */}
        <button className="row" type="button" data-interactive="true" onClick={onSignOut}>
          <span className="l">
            <Icon name="log-out" size={15} /> {t('signOut')}
          </span>
          <span className="r">
            <Icon name="chevron-right" size={14} />
          </span>
        </button>
        {/*
          A5 (`ui-scale-refresh-handoff.md`) — the sidebar foot. `margin-block-start: auto` on
          `.drawer-version` pushes it to the bottom of `.drawer`'s own flex column; nothing here
          is a fixed footer height, so there is nothing to overflow as the rows above it grow.
          The version stamp moved from `AboutScreen` (D136 put the version there once; it is text
          now, not a row — reading it is not an action) and is never tappable.
        */}
        <div className="drawer-version">
          <span>
            {t('versionLabel')} · <Bidi>{BUILD.commit}</Bidi>
          </span>
          {buildDate === undefined ? null : (
            <span>
              {buildDate.day} <bdi>{buildDate.month}</bdi> {buildDate.year}
            </span>
          )}
        </div>
      </aside>
    </>
  )
}
